import { ELoanUsage, EUserFlow, EVehicleAssetType } from '@/context/borrower';

import { DEFAULT_PARTNER } from './defaultPartner';
import { EPartners, IPartnerParams } from './partnerTypes';

export const CAREXPERT_PARTNER_NAME = 'CarExpert';

/**
 * A local stand-in for CarExpert's real configuration.
 *
 * The live journey is configured in the database and reaches the app at
 * runtime; this exists so the screens can be built and reviewed locally
 * without a backend. The values below were read from the live config for
 * vroom code M6307 on 24/09/2026.
 *
 * Two deliberate differences from production:
 *
 *  - leadEmail is left unset. A real one here would let a local test session
 *    mail CarExpert's actual inbox. dealerEmail and dealerPhone are copied
 *    from live because the client only ever displays them.
 *  - waterfallLenders is inherited rather than copied. Ten of the lender ids
 *    in the live config have no member in EMatchingEngineLenders, so they
 *    cannot be expressed here; lender matching is the database's job anyway.
 */
export const CAREXPERT_PARTNER: IPartnerParams = {
    ...DEFAULT_PARTNER,
    id: EPartners.CAREXPERT,
    dealerName: CAREXPERT_PARTNER_NAME,
    whiteLabel: true,

    // Brand
    primaryColor: '#ef3933',
    secondaryColor: '#000000',
    tertiaryColor: '#000000',
    quaternaryColor: '#000000',
    logo: 'https://youx-assets.s3.ap-southeast-2.amazonaws.com/vroom/assets/dog_1789103331483_CarExpert_Logo_Red-1024x269.png',

    // Journey shape
    userFlow: EUserFlow.EARLY_SIGNUP,
    loanUsage: ELoanUsage.PERSONAL,
    loanTerm: 5,
    hideSelectVehicleScreen: true,
    hideSellerScreen: true,
    skipExpensesScreen: true,
    earlyReferredScreen: true,
    showQuickQuote: true,
    showNewOrUsedCarToggle: true,
    skipPreapproval: false,
    isNovatedLease: false,
    disabledGlassesGuideVinLookup: false,

    /*
     * Turns on the email field in the lead capture modal. The field and its
     * validation already exist behind this flag; the live config simply has
     * no signupOptions, so it never renders.
     *
     * Mirrored here so the screen can be reviewed locally. The change that
     * matters is the same key on the partner record.
     */
    signupOptions: {
        zipcode: false,
        customerEmail: true,
        storesId: '',
        stores: [],
    },

    // Asset defaults
    defaultVehicleAssetType: EVehicleAssetType.MOTOR_VEHICLES_SEC,

    // Displayed on the affordability, referred and declined screens. Copied
    // from live, spacing included: the number is rendered exactly as configured.
    dealerEmail: 'assetfinance@carexpert.com.au',
    dealerPhone: '1300 640 915',
    termsAndConditionsLink: 'https://www.youxapply.com.au/privacy',

    /*
     * quickQuotePrivacyPolicy is deliberately NOT set here.
     *
     * It is the partner's disclaimer and lives in their config record. A copy
     * in code would go stale the moment they edit it, and would then be what
     * production served if it ever fell through to this fallback. The modal
     * reads it from config either way; locally it is simply empty.
     */

    /*
     * Off everywhere except a local session that opts in.
     *
     * This config is a fallback: production reaches it if lookupVroomCode
     * returns nothing for a carexpert URL, so a hardcoded true would put the
     * unsigned-off copy in front of real people. REACT_APP_CAREXPERT_COPY is
     * set only in .env.local, so the flag is false in every deployed build
     * and the real switch stays the partner record in the database.
     */
    useCarExpertCopy: process.env.REACT_APP_CAREXPERT_COPY === 'true',
};
